import { useCallback, useEffect, useRef, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import {
  Sparkle, MagnifyingGlass, X, DownloadSimple, CalendarBlank, ArrowRight, CircleNotch,
} from "@phosphor-icons/react";

const CATEGORY_META = {
  transaction: { label: "Transaction", color: "#2456A6", bg: "#DCEAFB" },
  ledger: { label: "Ledger", color: "#0F766E", bg: "#D8F0ED" },
  plaid: { label: "Plaid", color: "#4338CA", bg: "#E4E2FB" },
  settings: { label: "Settings", color: "#52525B", bg: "#ECECEE" },
  rowan: { label: "Rowan", color: "#8A6A3B", bg: "#F6EEDF" },
};
const DATE_PRESETS = [
  { key: "7d", label: "Last 7 days" }, { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" }, { key: "month", label: "This month" },
  { key: "lastmonth", label: "Last month" }, { key: "all", label: "All time" },
];

function presetRange(key) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "7d") return [iso(new Date(now - 7 * 864e5)), ""];
  if (key === "30d") return [iso(new Date(now - 30 * 864e5)), ""];
  if (key === "90d") return [iso(new Date(now - 90 * 864e5)), ""];
  if (key === "month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), ""];
  if (key === "lastmonth") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return [iso(first), iso(last)];
  }
  return ["", ""];
}

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function absTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function Avatar({ name, isRowan }) {
  if (isRowan) {
    return (
      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--bk-rowan-bg)" }}>
        <Sparkle size={16} weight="duotone" color="var(--bk-accent)" />
      </span>
    );
  }
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-white" style={{ background: "var(--bk-accent-2, #4A6B53)" }}>
      {initials}
    </span>
  );
}

export function ActivityTab({ businessId, canViewAll }) {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [selMembers, setSelMembers] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [preset, setPreset] = useState("30d");
  const [customRange, setCustomRange] = useState(["", ""]);
  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [jumpDate, setJumpDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const nextBeforeRef = useRef("");
  const sentinelRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const [dateFrom, dateTo] = preset === "custom" ? customRange : presetRange(preset);

  const buildParams = useCallback((before) => {
    const p = new URLSearchParams();
    if (selMembers.length) p.set("members", selMembers.join(","));
    if (selCats.length) p.set("categories", selCats.join(","));
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (debouncedQ) p.set("q", debouncedQ);
    if (before) p.set("before", before);
    return p.toString();
  }, [selMembers, selCats, dateFrom, dateTo, debouncedQ]);

  const load = useCallback(async (append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/activity/feed?${buildParams(append ? nextBeforeRef.current : jumpDate ? `${jumpDate}T23:59:59` : "")}`);
      setEvents((prev) => (append ? [...prev, ...data.events] : data.events));
      setHasMore(data.has_more);
      nextBeforeRef.current = data.next_before || "";
      if (data.members?.length) setMembers(data.members);
    } catch {
      toast.error("Couldn't load activity.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, buildParams, jumpDate]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) load(true);
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, load]);

  const dismissNote = async (eventId, noteId) => {
    setEvents((es) => es.map((e) => (e.id === eventId ? { ...e, annotations: e.annotations.filter((n) => n.id !== noteId) } : e)));
    try { await api.post(`/businesses/${businessId}/activity/annotations/${noteId}/dismiss`); } catch { /* hidden locally */ }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/businesses/${businessId}/activity/export?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ironroot-activity-log.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch {
      toast.error("Export failed.");
    }
  };

  const toggle = (list, setList, val) => setList(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);
  const nonDefault = selMembers.length || selCats.length || preset !== "30d" || debouncedQ || jumpDate;
  const clearAll = () => { setSelMembers([]); setSelCats([]); setPreset("30d"); setCustomRange(["", ""]); setSearch(""); setJumpDate(""); };

  const chips = [
    ...selMembers.map((id) => ({ key: `m-${id}`, label: members.find((m) => m.member_id === id)?.name || "Member", clear: () => toggle(selMembers, setSelMembers, id) })),
    ...selCats.map((c) => ({ key: `c-${c}`, label: CATEGORY_META[c]?.label || c, clear: () => toggle(selCats, setSelCats, c) })),
    ...(preset !== "30d" ? [{ key: "d", label: DATE_PRESETS.find((p) => p.key === preset)?.label || "Custom dates", clear: () => { setPreset("30d"); setCustomRange(["", ""]); } }] : []),
    ...(debouncedQ ? [{ key: "q", label: `"${debouncedQ}"`, clear: () => setSearch("") }] : []),
    ...(jumpDate ? [{ key: "j", label: `From ${jumpDate}`, clear: () => setJumpDate("") }] : []),
  ];

  return (
    <div data-testid="activity-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>Activity</h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>
            {canViewAll ? "Every action in this workspace — who did it, what changed, and when." : "Your activity in this workspace."}
          </p>
        </div>
        {canViewAll && (
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-semibold hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} data-testid="activity-export-btn">
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
        )}
      </div>

      <div className="bk-card p-4 mb-4 space-y-3" data-testid="activity-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2" color="var(--bk-text-2)" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search activity…" className="w-full pl-9 pr-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--bk-border)" }} data-testid="activity-search-input" />
          </div>
          <select value={preset} onChange={(e) => setPreset(e.target.value)} className="px-3 py-2 rounded-md border text-sm bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="activity-date-preset">
            {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            <option value="custom">Custom range</option>
          </select>
          {preset === "custom" && (
            <>
              <DateInput value={customRange[0]} onChange={(e) => setCustomRange([e.target.value, customRange[1]])} className="px-2 py-2 rounded-md border text-sm" style={{ borderColor: "var(--bk-border)" }} data-testid="activity-date-from" />
              <DateInput value={customRange[1]} onChange={(e) => setCustomRange([customRange[0], e.target.value])} className="px-2 py-2 rounded-md border text-sm" style={{ borderColor: "var(--bk-border)" }} data-testid="activity-date-to" />
            </>
          )}
          <div className="inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--bk-text-2)" }}>
            <CalendarBlank size={15} /> Jump to
            <DateInput value={jumpDate} onChange={(e) => setJumpDate(e.target.value)} className="px-2 py-1.5 rounded-md border text-sm" style={{ borderColor: "var(--bk-border)" }} data-testid="activity-jump-date" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <button key={key} onClick={() => toggle(selCats, setSelCats, key)} data-testid={`activity-cat-${key}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={selCats.includes(key) ? { background: meta.color, borderColor: meta.color, color: "#fff" } : { borderColor: "var(--bk-border)", color: meta.color, background: meta.bg }}>
              {meta.label}
            </button>
          ))}
        </div>
        {canViewAll && members.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="activity-member-filter">
            {members.map((m) => (
              <button key={m.member_id} onClick={() => toggle(selMembers, setSelMembers, m.member_id)} data-testid={`activity-member-${m.member_id}`}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors"
                style={selMembers.includes(m.member_id) ? { background: "var(--bk-text)", borderColor: "var(--bk-text)", color: "#fff" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                {m.role === "system" && <Sparkle size={12} weight="duotone" color={selMembers.includes(m.member_id) ? "#F0C36D" : "var(--bk-accent)"} />}
                {m.name}
              </button>
            ))}
          </div>
        )}
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="activity-filter-chips">
            {chips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: "var(--bk-page)", color: "var(--bk-text)" }}>
                {c.label}
                <button onClick={c.clear} aria-label={`Remove filter ${c.label}`}><X size={11} weight="bold" /></button>
              </span>
            ))}
            {nonDefault && <button onClick={clearAll} className="text-xs font-semibold hover:underline ml-1" style={{ color: "var(--bk-accent)" }} data-testid="activity-clear-filters">Clear all filters</button>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="activity-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bk-card p-4 animate-pulse"><div className="h-4 w-2/3 rounded" style={{ background: "var(--bk-page)" }} /><div className="h-3 w-1/3 rounded mt-2" style={{ background: "var(--bk-page)" }} /></div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bk-card p-10 text-center" data-testid="activity-empty-state">
          <Sparkle size={32} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }}>
            {nonDefault ? "No events match your current filters." : "No activity in this period. Try expanding the date range."}
          </p>
          {nonDefault && <button onClick={clearAll} className="text-sm font-semibold hover:underline mt-2" style={{ color: "var(--bk-accent)" }}>Clear all filters</button>}
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="activity-feed-list">
          {events.map((e) => {
            const meta = CATEGORY_META[e.category] || CATEGORY_META.ledger;
            const isRowan = e.actor_id === "rowan-system";
            return (
              <div key={e.id} className="bk-card p-4" data-testid={`activity-feed-event-${e.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar name={e.actor_name} isRowan={isRowan} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{e.actor_name}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                      {e.grouped && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>{e.group_count} grouped</span>}
                      <span className="text-xs ml-auto shrink-0" style={{ color: "var(--bk-disabled)" }} title={absTime(e.created_at)}>{relTime(e.created_at)}</span>
                    </div>
                    <p className="text-sm mt-1" style={{ color: "var(--bk-text)" }}>{e.description}</p>
                    {(e.changed_fields || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`activity-diff-${e.id}`}>
                        {e.changed_fields.map((f) => (
                          <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
                            {f}: {String(e.before?.[f] ?? "—")} <ArrowRight size={9} weight="bold" /> {String(e.after?.[f] ?? "—")}
                          </span>
                        ))}
                      </div>
                    )}
                    {(e.annotations || []).map((n) => (
                      <div key={n.id} className="flex items-start gap-2 mt-2 ml-1 pl-3 border-l-2" style={{ borderColor: "rgba(232,168,56,0.5)" }} data-testid={`activity-annotation-${n.id}`}>
                        <Sparkle size={13} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
                        <p className="text-xs flex-1" style={{ color: "var(--bk-text-2)" }}>{n.text}</p>
                        {canViewAll && (
                          <button onClick={() => dismissNote(e.id, n.id)} data-testid={`dismiss-annotation-${n.id}`} aria-label="Dismiss note" className="shrink-0">
                            <X size={12} weight="bold" color="var(--bk-disabled)" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={sentinelRef} />
          {loadingMore && (
            <div className="flex justify-center py-4" data-testid="activity-loading-more">
              <CircleNotch size={20} className="animate-spin" color="var(--bk-accent)" />
            </div>
          )}
          {!hasMore && events.length >= 10 && (
            <p className="text-xs text-center py-3" style={{ color: "var(--bk-disabled)" }}>You've reached the end of this period.</p>
          )}
        </div>
      )}
    </div>
  );
}
